/**
 * Bulk Order item rows for the Globo contact form.
 *
 * Globo's repeater only repeats a single input, but the brief wants rows of
 * Part SKU / Brand / Quantity with a "+ Add SKU / Item" button (max 10). So the
 * form has one plain textarea (custom class `lc-item-rows`, or field textarea-4 on
 * form 42758) and this script hides it behind our own rows, writing the list back
 * into it as "1. SKU | Brand | Qty 4" lines. Globo submits the textarea like any
 * other field. If this script never runs, the textarea simply shows instead.
 *
 * Globo renders forms client-side, so we watch the DOM and mount when the field appears.
 */
(() => {
  const FIELD = '.lc-item-rows textarea, textarea.lc-item-rows, textarea#\\34 2758-textarea-4';
  const MAX_ROWS = 10;
  const COLUMNS = [
    { key: 'sku', label: 'Part SKU / Model Number' },
    { key: 'brand', label: 'Brand (Optional)' },
    { key: 'qty', label: 'Quantity Required' }
  ];
  const ERROR_TEXT = 'Please enter a Part SKU / Model Number and Quantity Required for each item.';

  const injectStyles = () => {
    if (document.getElementById('lc-item-rows-css')) return;
    const style = document.createElement('style');
    style.id = 'lc-item-rows-css';
    style.textContent = `
      .lc-ir__row { display: grid; grid-template-columns: 1fr 1fr 110px 36px; gap: 12px; align-items: end; margin-bottom: 12px; }
      .lc-ir__cell { display: flex; flex-direction: column; min-width: 0; }
      .lc-ir__cell .globo-label { margin-bottom: 6px; }
      .lc-ir__row:not(:first-child) .lc-ir__cell .globo-label { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
      .lc-ir__remove { height: 40px; border: 0; background: none; cursor: pointer; font-size: 18px; line-height: 1; color: inherit; }
      .lc-ir__remove[hidden] { display: block; visibility: hidden; }
      .lc-ir__add { border: 0; background: none; padding: 4px 0; cursor: pointer; text-decoration: underline; color: inherit; font: inherit; }
      .lc-ir__add[hidden] { display: none; }
      .lc-ir input.is-invalid { border-color: #d72c0d !important; }
      @media (max-width: 749px) {
        .lc-ir__row { grid-template-columns: 1fr 90px 36px; padding-bottom: 12px; border-bottom: 1px solid rgba(0,0,0,.1); }
        .lc-ir__cell--sku { grid-column: 1 / -1; }
        .lc-ir__row:not(:first-child) .lc-ir__cell .globo-label { position: static; width: auto; height: auto; overflow: visible; clip: auto; white-space: normal; }
      }
    `;
    document.head.appendChild(style);
  };

  const mount = (textarea) => {
    if (textarea.dataset.lcItemRows) return;
    textarea.dataset.lcItemRows = 'mounted';
    injectStyles();

    const control = textarea.closest('.globo-form-control') || textarea.parentElement;
    const inputWrap = textarea.closest('.globo-form-input') || textarea;
    const label = control.querySelector(':scope > label');
    const errorEl = control.querySelector('small.messages');
    const formId = textarea.id.split('-')[0] || 'lc';

    const root = document.createElement('div');
    root.className = 'lc-ir';
    const rows = document.createElement('div');
    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'lc-ir__add';
    add.textContent = '+ Add SKU / Item';
    root.append(rows, add);

    let uid = 0;
    const rowEls = () => Array.from(rows.children);

    const sync = () => {
      const lines = [];
      rowEls().forEach((row) => {
        const v = (k) => row.querySelector(`[data-key="${k}"]`).value.trim();
        if (!v('sku') && !v('brand') && !v('qty')) return;
        lines.push(`${lines.length + 1}. ${v('sku') || '—'} | ${v('brand') || '—'} | Qty ${v('qty') || '—'}`);
      });
      textarea.value = lines.join('\n');
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
      textarea.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const refresh = () => {
      const all = rowEls();
      all.forEach((row) => { row.querySelector('.lc-ir__remove').hidden = all.length === 1; });
      add.hidden = all.length >= MAX_ROWS;
    };

    const addRow = (focus) => {
      if (rowEls().length >= MAX_ROWS) return;
      uid += 1;
      const row = document.createElement('div');
      row.className = 'lc-ir__row';
      COLUMNS.forEach((col) => {
        const id = `${formId}-lc-ir-${col.key}-${uid}`;
        const cell = document.createElement('div');
        cell.className = `lc-ir__cell lc-ir__cell--${col.key}`;
        const l = document.createElement('label');
        l.className = 'classic-label globo-label';
        l.htmlFor = id;
        l.textContent = col.label;
        const input = document.createElement('input');
        input.id = id;
        input.className = 'classic-input';
        input.dataset.key = col.key;
        if (col.key === 'qty') {
          input.type = 'number';
          input.min = '1';
          input.step = '1';
          input.inputMode = 'numeric';
        } else {
          input.type = 'text';
        }
        // No name attribute: only the textarea is submitted.
        cell.append(l, input);
        row.appendChild(cell);
      });
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'lc-ir__remove';
      remove.setAttribute('aria-label', 'Remove item');
      remove.textContent = '✕';
      row.appendChild(remove);
      rows.appendChild(row);
      refresh();
      if (focus) row.querySelector('input').focus();
    };

    rows.addEventListener('input', (e) => {
      e.target.classList.remove('is-invalid');
      sync();
    });
    rows.addEventListener('click', (e) => {
      const btn = e.target.closest('.lc-ir__remove');
      if (!btn) return;
      btn.closest('.lc-ir__row').remove();
      refresh();
      sync();
      rows.querySelector('input').focus();
    });
    add.addEventListener('click', () => addRow(true));

    // Rows with anything in them need a SKU and a quantity. Empty rows are ignored,
    // so customers who only describe their needs or upload a file can still continue.
    const validate = () => {
      let ok = true;
      rowEls().forEach((row) => {
        const sku = row.querySelector('[data-key="sku"]');
        const brand = row.querySelector('[data-key="brand"]');
        const qty = row.querySelector('[data-key="qty"]');
        const used = sku.value.trim() || brand.value.trim() || qty.value.trim();
        const badSku = used && !sku.value.trim();
        const badQty = used && !(parseInt(qty.value, 10) >= 1);
        sku.classList.toggle('is-invalid', !!badSku);
        qty.classList.toggle('is-invalid', !!badQty);
        if (badSku || badQty) ok = false;
      });
      if (errorEl) errorEl.textContent = ok ? '' : ERROR_TEXT;
      return ok;
    };

    // Run before Globo's own Next/Submit handler, and only while our step is showing.
    const form = textarea.closest('form');
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.action.next, .action.submit');
      if (!btn || !form || !form.contains(btn) || !root.isConnected) return;
      if (!root.offsetParent) return;
      sync();
      if (!validate()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        const first = root.querySelector('.is-invalid');
        if (first) first.focus();
      }
    }, true);

    // Globo resets the form after a successful send; start again from one empty row.
    if (form) {
      form.addEventListener('reset', () => {
        setTimeout(() => {
          rows.innerHTML = '';
          addRow(false);
          sync();
          if (errorEl) errorEl.textContent = '';
        });
      });
    }

    // Globo fixes an inline height on .panels when a step opens, so rows added
    // afterwards would slide under the Next/Submit buttons. Grow or shrink that
    // height by however much our field changes while the step is showing.
    const panels = control.closest('.panels');
    if (panels && window.ResizeObserver) {
      let last = 0;
      new ResizeObserver(() => {
        const h = control.offsetHeight;
        const fixed = parseFloat(panels.style.height);
        if (last && h && fixed) panels.style.height = `${fixed + h - last}px`;
        last = h;
      }).observe(control);
    }

    // Inline styles, not the hidden attribute, which Globo's CSS can override.
    if (label) label.style.display = 'none';
    inputWrap.style.display = 'none';
    inputWrap.after(root);
    addRow(false);
  };

  let queued = false;
  const scan = () => {
    queued = false;
    document.querySelectorAll(FIELD).forEach(mount);
  };

  scan();
  new MutationObserver(() => {
    if (!queued) {
      queued = true;
      requestAnimationFrame(scan);
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
